import { products } from '../data/products'
import { livingProducts } from '../data/livingProducts'
import { categories, collections, livingCategories, livingCollections } from '../data/collections'
import type { Collection, Line, Product } from '../types/product'

export const catalog: Product[] = [...products, ...livingProducts]

export function lineOf(product: Product): Line {
  return product.line ?? 'beauty'
}

export function productPath(product: Product): string {
  return lineOf(product) === 'living' ? `/living/product/${product.slug}` : `/product/${product.slug}`
}

export function collectionPath(slug: string, line: Line = 'beauty'): string {
  return line === 'living' ? `/living/collectie/${slug}` : `/collectie/${slug}`
}

export function getProduct(slug: string): Product | undefined {
  return catalog.find((p) => p.slug === slug)
}

export function fromPrice(product: Product): number {
  return Math.min(...product.variants.map((v) => v.price))
}

export function hasRange(product: Product): boolean {
  return new Set(product.variants.map((v) => v.price)).size > 1
}

export function categoryTitle(id: string): string {
  return [...categories, ...livingCategories].find((c) => c.id === id)?.title ?? ''
}

export function resolveCollection(slug: string, line: Line = 'beauty'): Collection | undefined {
  const pool = line === 'living' ? livingCollections : collections
  const found = pool.find((c) => c.slug === slug)
  if (found) return found
  const cat = (line === 'living' ? livingCategories : categories).find((c) => c.id === slug)
  if (!cat) return undefined
  return {
    slug: cat.id,
    title: cat.title,
    intro: `Alle producten in ${cat.title.toLowerCase()}.`,
    kind: 'category',
    value: cat.id,
    image: cat.image,
    line,
  }
}

export function productsForCollection(c: Collection): Product[] {
  const base = c.line === 'living' ? livingProducts : products
  const list = (() => {
    switch (c.kind) {
      case 'audience':
        return base.filter((p) => p.audiences.includes(c.value as Product['audiences'][number]))
      case 'badge':
        return base.filter((p) => p.badges.includes(c.value as Product['badges'][number]))
      case 'category':
        return base.filter((p) => p.category === c.value)
      default:
        return base
    }
  })()
  return [...list].sort((a, b) => b.reviews - a.reviews)
}

export function relatedProducts(product: Product, limit = 4): Product[] {
  const line = lineOf(product)
  const step = product.ritualStep
  const scored = catalog
    .filter((p) => lineOf(p) === line && p.slug !== product.slug && !product.includes?.includes(p.slug))
    .map((p) => {
      let score = p.reviews / 1000
      if (step && p.ritualStep && Math.abs(p.ritualStep - step) <= 2 && p.ritualStep !== step) score += 3
      if (p.category === product.category) score += 1
      if (p.slug === 'hammam-ritueel-set' || p.slug === 'thee-ceremonie-set') score += 2
      return { p, score }
    })
    .sort((a, b) => b.score - a.score)
  return scored.slice(0, limit).map((s) => s.p)
}

export function searchProducts(query: string, limit = 6, line?: Line): Product[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const pool = line ? [...catalog].sort((a, b) => Number(lineOf(b) === line) - Number(lineOf(a) === line)) : catalog
  return pool
    .filter((p) => `${p.name} ${p.subtitle} ${categoryTitle(p.category)}`.toLowerCase().includes(q))
    .slice(0, limit)
}
